import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import debounce from 'lodash/debounce';
import { Popup } from '@progress/kendo-react-popup';
import api from '@utils/axios';
import { value } from 'lodash/seq';
import { lookupPath } from '../../routes';
import total from '../Dashboards/InvoiceInsights/nested/Total';

// For use with rails nested_fields_for
// showQboIcons / showQbdIcons: IP-12975 — the picked item's QuickBooks badge follows the
// integration that is on, so a QBD-origin item on a QBO-only tenant renders no badge.
function InvoiceMaterialServicesAutocomplete({
  rowName,
  value,
  lineItemItem,
  initSearch,
  isReadOnly = false,
  showQboIcons = true,
  showQbdIcons = true
}) {
  const [isLoading, setIsLoading] = useState(false);
  const [inputValue, setInputValue] = useState(initSearch);
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const [readOnly, setReadonly] = useState(isReadOnly);
  const [plDropdownOpen, plSetDropdownOpen] = useState(false);
  const [selectedValue, setSelectedValue] = useState(value);
  const [page, setPage] = useState(0);
  const activePriceLevel = selectedValue?.priceLevels?.find((x) => x.selected);
  const rowCorrectionId = useRef(lineItemItem); // Only for edits
  const [delay, setDelay] = useState(800);
  const totalRows = useRef(0);

  const [options, setOptions] = useState([]);

  const anchor = useRef(null);
  const popupRef = useRef(null);
  const priceLevelAnchor = useRef(null);
  const priceLevelPopupRef = useRef(null);

  useEffect(() => {
    const closeDropdownOnScroll = (e) => {
      setDropdownOpen(false);
      plSetDropdownOpen(false);
    };

    const closeDropdownOnBlur = (e) => {
      if (anchor.current?.contains(e.target)) return;
      if (priceLevelAnchor.current?.contains(e.target)) return;
      if (!popupRef.current?.contains(e.target)) setDropdownOpen(false);
      if (!priceLevelPopupRef.current?.contains(e.target)) plSetDropdownOpen(false);
    };

    document.querySelector('.partition__workspace').addEventListener('scroll', closeDropdownOnScroll);
    document.body.addEventListener('click', closeDropdownOnBlur);

    return () => {
      document.querySelector('.partition__workspace').removeEventListener('scroll', closeDropdownOnScroll);
      document.body.removeEventListener('click', closeDropdownOnBlur);
    };
  }, [selectedValue, plDropdownOpen]);

  const handleChange = (event) => {
    const { value } = event.target;
    setInputValue(value);

    setProductField(value);
    debounceLookupMaterialService(value);
  };

  const handleClick = (event) => {
    const { value } = event.target;
    setInputValue(value);

    setProductField(value);
    lookupMaterialService(value);
  };

  const handleSelection = (selectedOption) => {
    const pl = selectedOption?.priceLevels?.find((x) => x.selected);
    setSelectedValue(selectedOption);
    setDropdownOpen(false);
    plSetDropdownOpen(false);

    const proposed_correction_field = document.querySelector(`[name*="${rowName}[proposed_correction_id]"]`);
    const price_level_field = document.querySelector(`[name*="${rowName}[price_level_id]"]`);
    const product_field = document.querySelector(`[name*="${rowName}[product]"]`);

    proposed_correction_field.value = selectedOption.id;
    price_level_field.value = pl?.id;
    product_field.value = selectedOption.label;

    $(`[name*="${rowName}[qbd_id]"]`).val('');
    $(`[name*="${rowName}[qbd_id]"]`).siblings('.custom-icon--qbo').remove();
    $(`[name*="${rowName}[qbd_id]"]`).val('');
    $(`[name*="${rowName}[qbd_id]"]`).siblings('.custom-icon--qbo').remove();

    $(`[name*="${rowName}[description]"]`).val(selectedOption.description);
    $(`[name*="${rowName}[unit_price]"]`).val(pl?.price || selectedOption.unit_price);
    $(`[name*="${rowName}[is_taxable]"]`).prop('checked', selectedOption.taxable);

    if (selectedOption.qbo_id) {
      // $(`#${e.sender.element[0].id}`).prop('is_qb', true)
      $(`[name*="${rowName}[qbo_id]"]`).val(selectedOption.qbo_id);
      if (showQboIcons) {
        $(`[name*="${rowName}[qbo_id]"]`).after(
          `<img height="16" width="16" src="/qbo.svg" class="custom-icon--qbo mt-2" title="QuickBooks" />`
        );
      }
    } else if (selectedOption.qbd_id) {
      // $(`#${e.sender.element[0].id}`).prop('is_qb', true)
      $(`[name*="${rowName}[qbd_id]"]`).val(selectedOption.qbd_id);
      if (showQbdIcons) {
        $(`[name*="${rowName}[qbd_id]"]`).after(
          `<img height="16" width="16" src="/qbo.svg" class="custom-icon--qbo mt-2" title="QuickBooks" />`
        );
      }
    } else {
      // $(`#${e.sender.element[0].id}`).prop('is_qb', false)
    }

    update_total();
  };

  const handleSelectionClear = () => {
    setSelectedValue(null);
    setInputValue('');

    const proposed_correction_field = document.querySelector(`[name*="${rowName}[proposed_correction_id]"]`);
    const price_level_field = document.querySelector(`[name*="${rowName}[price_level_id]"]`);
    const product_field = document.querySelector(`[name*="${rowName}[product]"]`);

    proposed_correction_field.value = null;
    price_level_field.value = null;
    product_field.value = null;

    $(`[name*="${rowName}[qbd_id]"]`).val('');
    $(`[name*="${rowName}[qbd_id]"]`).siblings('.custom-icon--qbo').remove();
    $(`[name*="${rowName}[qbd_id]"]`).val('');
    $(`[name*="${rowName}[qbd_id]"]`).siblings('.custom-icon--qbo').remove();

    $(`[name*="${rowName}[description]"]`).val(null);
    $(`[name*="${rowName}[unit_price]"]`).val(null);
    $(`[name*="${rowName}[is_taxable]"]`).prop('checked', false);

    update_total();
  };

  const debounceLookupMaterialService = useCallback(
    debounce((value) => {
      setProductField(value);
      lookupMaterialService(value);
    }, 800),
    []
  );

  const setProductField = (value) => {
    const product_field = document.querySelector(`[name*="${rowName}[product]"]`);
    product_field.value = value;
  };

  const lookupMaterialService = (value) => {
    setIsLoading(true);
    setPage(0);

    const params = {
      type: 'invoice_material_and_services',
      search_value: value,
      line_item_id: rowCorrectionId.current,
      take: 20,
      skip: 0
    };

    api
      .get(lookupPath(), { params, timeout: 0 })
      .then((response) => {
        totalRows.current = response.data.total_count;
        setOptions(response.data.data);
      })
      .catch((error) => {
        console.log(error);
        totalRows.current = 0;
        setOptions([]);
      })
      .finally(() => {
        setIsLoading(false);
        setDropdownOpen(true);
      });
  };

  return (
    <>
      {selectedValue && (
        <div
          className="qmb-input --x-full"
          style={{
            display: 'flex',
            padding: '8px',
            boxSizing: 'border-box',
            alignItems: 'center',
            width: '24rem',
            backgroundColor: readOnly ? '#f5f5f5' : ''
          }}>
          <div style={{ display: 'flex', flexDirection: 'column', flexGrow: 1 }}>
            <small>{selectedValue.type}</small>
            <div style={{ display: 'flex', flexWrap: 'wrap', columnGap: '.5rem', alignItems: 'baseline' }}>
              {selectedValue.label}
              {selectedValue?.priceLevels?.length > 0 && (
                <button
                  ref={priceLevelAnchor}
                  type="button"
                  className="qmb-control"
                  style={{
                    padding: '3px 6px',
                    borderRadius: '10px',
                    pointerEvents: readOnly ? 'none' : ''
                  }}
                  onClick={() => plSetDropdownOpen(!plDropdownOpen)}>
                  <i className="fa-light fa-diagram-subtask" style={{ fontSize: '1.2rem' }} />
                  <small>{activePriceLevel ? activePriceLevel.label : 'Price Level'}</small>
                </button>
              )}
            </div>
          </div>
          {!readOnly && (
            <button className="qmb-control--icon" onClick={handleSelectionClear}>
              <i className="fa-light fa-xmark" />
            </button>
          )}
        </div>
      )}

      {!selectedValue && (
        <div className="qmb-input--text --x-full" ref={anchor} data-invalid="true">
          {isLoading && <Spinner />}
          <input value={inputValue} onChange={handleChange} onClick={handleClick} placeholder="Search..." />
        </div>
      )}

      <Popup anchor={priceLevelAnchor.current} show={plDropdownOpen}>
        <PriceLevelsDropdown
          popupRef={priceLevelPopupRef}
          selectedValue={selectedValue}
          handleSelection={handleSelection}
        />
      </Popup>

      <Popup anchor={anchor.current} show={dropdownOpen}>
        <Dropdown
          closeDropdown={() => setDropdownOpen(false)}
          popupRef={popupRef}
          options={options}
          totalRows={totalRows}
          setOptions={setOptions}
          value={inputValue}
          handleSelection={handleSelection}
          rowCorrectionId={rowCorrectionId}
          page={page}
          setPage={setPage}
        />
      </Popup>

      <input type="hidden" />
    </>
  );
}

function PriceLevelsDropdown({ popupRef, selectedValue, handleSelection }) {
  const handleSelectPriceLevel = (event, selectedId) => {
    event.stopPropagation();
    event.preventDefault();
    handleSelection({
      ...selectedValue,
      priceLevels: selectedValue?.priceLevels?.map((pl) => ({ ...pl, selected: pl.id === selectedId }))
    });
  };

  return (
    <div tabIndex={0} className="qmb-popup" ref={popupRef} style={{ maxWidth: '400px' }}>
      <div
        style={{
          display: 'flex',
          flexDirection: 'column',
          maxHeight: '220px',
          overflowY: 'auto',
          width: '200px'
        }}>
        {selectedValue.priceLevels.map((x) => (
          <button className="qmb-control" onClick={(e) => handleSelectPriceLevel(e, x.id)} disabled={x.selected}>
            {x.label}
          </button>
        ))}

        {selectedValue.priceLevels.some((x) => x.selected) && (
          <button className="qmb-control" onClick={(e) => handleSelectPriceLevel(e, null)}>
            <i className="fa-light fa-xmark" />
            Remove Price Level
          </button>
        )}
      </div>
    </div>
  );
}

function Dropdown({
  closeDropdown,
  popupRef,
  options,
  totalRows,
  setOptions,
  value,
  handleSelection,
  rowCorrectionId,
  page,
  setPage
}) {
  function Content() {
    if (options.length > 0) {
      return options.map((o) => <SelectOption data={o} handleSelection={handleSelection} />);
    }

    return null;
  }

  function Pagination() {
    const [isVisible, setIsVisible] = useState(false);
    const targetRef = useRef(null);

    useEffect(() => {
      const observer = new IntersectionObserver(
        (entries) => {
          if (entries[0].isIntersecting) {
            setIsVisible(true);
          } else {
            setIsVisible(false);
          }
        },
        {
          root: null,
          rootMargin: '0px',
          threshold: 1.0
        }
      );

      if (targetRef.current) {
        observer.observe(targetRef.current);
      }

      return () => {
        if (targetRef.current) {
          observer.unobserve(targetRef.current);
        }
      };
    }, []);

    useEffect(() => {
      if (isVisible) {
        const params = {
          type: 'proposal_material_and_services',
          search_value: value,
          take: 20,
          skip: (page + 1) * 20,
          proposal_correction_id: rowCorrectionId.current
        };

        api
          .get(lookupPath(), { params, timeout: 0 })
          .then((response) => {
            setOptions((prevData) => [...prevData, ...response.data.data]);
            setPage(page + 1);
          })
          .catch((error) => {
            console.log(error);
            totalRows.current = 0;
            setOptions([]);
          });
      }
    }, [isVisible]);

    if (totalRows.current === options.length) {
      return null;
    }

    return (
      <span ref={targetRef} style={{ display: 'flex', justifyContent: 'center', padding: '2rem' }}>
        <div id="spinner" className="qmb-loading--16" data-title="Loading...">
          <svg role="img">
            <use href="/map.svg#load-spinner" />
          </svg>
        </div>
      </span>
    );
  }

  return (
    <div tabIndex={0} className="qmb-popup" ref={popupRef} style={{ maxWidth: '400px' }}>
      <div
        style={{
          display: 'flex',
          flexDirection: 'column',
          maxHeight: '220px',
          overflowY: 'auto',
          width: '200px'
        }}>
        <Content />
        <Pagination />
      </div>
    </div>
  );
}

function SelectOption({ data, handleSelection }) {
  const handleSelectOption = (event) => {
    event.stopPropagation();
    event.preventDefault();
    handleSelection(data);
  };
  const handleSelectSubOption = (event, subOption) => {
    event.stopPropagation();
    event.preventDefault();

    subOption.selected = true;
    handleSelection(data);
  };

  return (
    <button
      className="qmb-control"
      style={{
        width: '100%',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'start',
        gap: 0
      }}
      onClick={(e) => handleSelectOption(e)}>
      <small>{data.type}</small>
      <span>{data.label}</span>
      {data.priceLevels?.map((priceLevel, index) => (
        <SubOption
          priceLevel={priceLevel}
          index={index}
          totalCount={data.priceLevels.length}
          handleClick={handleSelectSubOption}
        />
      ))}
    </button>
  );
}

function SubOption({ priceLevel, index, totalCount, handleClick }) {
  return (
    <button
      className="qmb-control"
      style={{
        marginTop: index === 0 ? '.5rem' : 'auto',
        width: '100%',
        display: 'flex',
        alignItems: 'start',
        gap: '2px',
        padding: '0px',
        color: '#666'
      }}
      onClick={(e) => handleClick(e, priceLevel)}>
      <span style={{ fontFamily: 'monospace' }}>{index === totalCount - 1 ? '└─' : '├─'}</span>
      <small>{priceLevel.label}</small>
    </button>
  );
}

function Spinner() {
  return (
    <div
      id="spinner"
      className="qmb-loading--16"
      style={{
        position: 'absolute',
        right: '5px',
        top: '50%',
        transform: 'translateY(-50%)',
        zIndex: 10
      }}>
      <svg role="img">
        <use href="/map.svg#load-spinner" />
      </svg>
    </div>
  );
}

InvoiceMaterialServicesAutocomplete.propTypes = {
  rowName: PropTypes.string.isRequired,
  value: PropTypes.object,
  lineItemItem: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
  initSearch: PropTypes.string,
  isReadOnly: PropTypes.bool,
  showQboIcons: PropTypes.bool,
  showQbdIcons: PropTypes.bool
};

InvoiceMaterialServicesAutocomplete.defaultProps = {
  value: null,
  lineItemItem: null,
  initSearch: '',
  isReadOnly: false,
  showQboIcons: true,
  showQbdIcons: true
};

export default InvoiceMaterialServicesAutocomplete;
